import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, downloadWithToast, formatApiErrorDetail } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ArrowsClockwise, Upload, FilePdf, FileZip, PencilLine, Trash, GearSix, Users, LockSimple, IdentificationBadge, CalendarCheck,
} from "@phosphor-icons/react";
import PayrollRunSheet from "@/components/hr/PayrollRunSheet";
import PayrollSettingsDialog from "@/components/hr/PayrollSettingsDialog";
import LivePunchBoard from "@/components/hr/LivePunchBoard";
import LeaveRequestsPanel from "@/components/hr/LeaveRequestsPanel";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const periodOf = (r) => `${MONTHS[(r.month || 1) - 1]} ${r.year}`;

export function AdminOnly() {
  return (
    <div className="p-10 text-center" data-testid="payroll-admin-only">
      <LockSimple size={28} className="mx-auto text-slate-400" />
      <div className="mt-2 text-sm font-semibold text-slate-700">Payroll is restricted to administrators</div>
      <div className="text-[11.5px] text-slate-500">Salary data is sensitive. Ask an admin to grant you access.</div>
    </div>
  );
}

export default function Payroll() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [openRun, setOpenRun] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [{ data: r }, { data: s }] = await Promise.all([api.get("/payroll/runs"), api.get("/payroll/stats")]);
      setRuns(r || []);
      setStats(s);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { if (user?.role === "admin") load(); }, [load, user]);

  if (user?.role !== "admin") return <AdminOnly />;

  const upload = async (file, replace = false) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/payroll/runs/import-register", fd, {
        headers: { "Content-Type": "multipart/form-data" }, params: { replace },
      });
      toast.success(`Imported ${data.rows.length} employees · ${periodOf(data)} · Net ${fmtMoney(data.totals.net_payable)}`);
      await load();
      setOpenRun(data.id);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      if (e.response?.status === 409 && !replace && /already exists/i.test(msg)) {
        if (window.confirm(`${msg}\n\nReplace the existing run with this file?`)) return upload(file, true);
      } else toast.error(msg);
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the ${periodOf(r)} payroll run? Employee master records are kept.`)) return;
    try { await api.delete(`/payroll/runs/${r.id}`); toast.success("Run deleted"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const latest = stats?.latest_run;
  return (
    <div className="space-y-4 p-4" data-testid="payroll-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold font-display text-slate-900 tracking-tight">Payroll &amp; Salary Slips</h1>
          <div className="text-[11.5px] text-slate-500">
            Import the monthly Salary Register (Form No. 13) → mark attendance → sync → issue premium letterhead salary slips.
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="h-8" onClick={() => navigate("/payroll/attendance")} data-testid="payroll-attendance-btn">
            <CalendarCheck size={13} className="mr-1" /> Attendance Register
          </Button>
          <Button variant="outline" size="sm" className="h-8" onClick={() => navigate("/payroll/employees")} data-testid="payroll-employees-btn">
            <IdentificationBadge size={13} className="mr-1" /> Employee Master
          </Button>
          <Button variant="outline" size="sm" className="h-8" onClick={() => setSettingsOpen(true)} data-testid="payroll-settings-btn">
            <GearSix size={13} className="mr-1" /> Letterhead &amp; Statutory
          </Button>
          <Button variant="ghost" size="sm" className="h-8" onClick={load} data-testid="payroll-refresh"><ArrowsClockwise size={12} className="mr-1" /> Refresh</Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.xls" className="hidden" data-testid="payroll-upload-input"
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }} />
          <Button size="sm" className="h-8 bg-[#EA580C] hover:bg-[#c94d0a] text-white" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="payroll-upload-btn">
            <Upload size={13} className="mr-1" /> {uploading ? "Importing…" : "Import Salary Register"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="payroll-kpis">
        <Kpi label="Active Employees" value={stats?.employees_active ?? "—"} sub={`${stats?.employees_total ?? 0} on master`} icon={Users} />
        <Kpi label="Latest Run" value={latest ? periodOf(latest) : "—"} sub={latest ? `${latest.totals?.employees} employees · ${latest.status}` : "Import a register to begin"} />
        <Kpi label="Latest Net Payout" value={latest ? fmtMoney(latest.totals?.net_payable) : "—"} sub={latest ? `Gross ${fmtMoney(latest.totals?.gross)}` : ""} mono />
        <Kpi label={`Net Paid · ${stats?.ytd_year || "YTD"}`} value={stats ? fmtMoney(stats.ytd_net_payable) : "—"} sub={`${stats?.runs_count ?? 0} payroll runs`} mono />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-3" data-testid="payroll-live-section">
        <div className="xl:col-span-3"><LivePunchBoard onOpenSettings={() => setSettingsOpen(true)} /></div>
        <div className="xl:col-span-2"><LeaveRequestsPanel /></div>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="text-[11.5px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-28">Period</TableHead>
                  <TableHead className="w-16 text-center">Days</TableHead>
                  <TableHead className="w-24 text-center">Employees</TableHead>
                  <TableHead className="text-right w-32">Gross</TableHead>
                  <TableHead className="text-right w-28">Deductions</TableHead>
                  <TableHead className="text-right w-32">Net Payable</TableHead>
                  <TableHead className="text-right w-28">PF + ESI (Er)</TableHead>
                  <TableHead className="text-center w-24">Status</TableHead>
                  <TableHead className="w-64">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-10">
                    {busy ? "Loading…" : "No payroll runs yet. Import your monthly Salary Register Excel to generate slips."}
                  </TableCell></TableRow>
                )}
                {runs.map((r) => (
                  <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`payroll-run-row-${r.year}-${r.month}`}>
                    <TableCell className="font-semibold text-slate-900">{periodOf(r)}</TableCell>
                    <TableCell className="text-center font-mono-num">{r.days_in_month}</TableCell>
                    <TableCell className="text-center font-mono-num">{r.totals?.employees}</TableCell>
                    <TableCell className="text-right font-mono-num">{fmtMoney(r.totals?.gross)}</TableCell>
                    <TableCell className="text-right font-mono-num text-rose-700">{fmtMoney(r.totals?.total_deductions)}</TableCell>
                    <TableCell className="text-right font-mono-num font-bold">{fmtMoney(r.totals?.net_payable)}</TableCell>
                    <TableCell className="text-right font-mono-num text-slate-500">{fmtMoney((r.totals?.pf_employer || 0) + (r.totals?.esi_employer || 0))}</TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-[9.5px] font-semibold uppercase tracking-widest ${r.status === "finalised" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {r.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" onClick={() => setOpenRun(r.id)} data-testid={`payroll-open-${r.id}`}>
                          <PencilLine size={11} className="mr-0.5" /> Open
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" title="All slips · one PDF"
                                onClick={() => downloadWithToast(`/payroll/runs/${r.id}/slips.pdf`, `SalarySlips_All_${periodOf(r).replace(" ", "-")}.pdf`).catch(() => {})}
                                data-testid={`payroll-all-pdf-${r.id}`}>
                          <FilePdf size={11} className="mr-0.5" /> All Slips
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" title="ZIP · one PDF per employee"
                                onClick={() => downloadWithToast(`/payroll/runs/${r.id}/slips.zip`, `SalarySlips_${periodOf(r).replace(" ", "-")}.zip`).catch(() => {})}
                                data-testid={`payroll-zip-${r.id}`}>
                          <FileZip size={11} />
                        </Button>
                        {r.status !== "finalised" && (
                          <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(r)} data-testid={`payroll-delete-${r.id}`}>
                            <Trash size={11} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {openRun && <PayrollRunSheet runId={openRun} onClose={() => setOpenRun(null)} onChanged={load} />}
      {settingsOpen && <PayrollSettingsDialog onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, mono }) {
  return (
    <div className="border border-slate-200 bg-white rounded-sm px-3 py-2.5 hover:border-[#EA580C]/40 transition-colors" data-testid={`payroll-kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</div>
        {Icon && <Icon size={13} className="text-slate-400" />}
      </div>
      <div className={`text-[16px] font-bold text-slate-900 mt-0.5 ${mono ? "font-mono-num" : ""}`}>{value}</div>
      {sub && <div className="text-[10.5px] text-slate-500 truncate">{sub}</div>}
    </div>
  );
}
